import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Globe, Tag, Sun, Calendar, Flag, Plane, MapPin, Compass, Star, Umbrella } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import TravelCard from '../components/TravelCard';
// [NocheYDia previa] import AdvertisingBanner from '../components/AdvertisingBanner';
// [NocheYDia previa] import SoldSection from '../components/SoldSection';
// [NocheYDia previa] import AirlinesSection from '../components/AirlinesSection';
// [NocheYDia previa] import BestPriceSection from '../components/BestPriceSection';
// [NocheYDia previa] import EuropeSection from '../components/EuropeSection';
// [NocheYDia previa] import InspirationSection from '../components/InspirationSection';
// [NocheYDia previa] import NewsletterSection from '../components/NewsletterSection';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://opcionesargentinas.com';

function ScrollCarousel({ children }) {
  const ref = useRef(null);

  const scroll = (dir) => {
    if (!ref.current) return;
    const card = ref.current.querySelector('[data-card]');
    const cardWidth = card ? card.offsetWidth + 16 : 320;
    ref.current.scrollBy({ left: dir * cardWidth, behavior: 'smooth' });
  };

  return (
    <div className="relative group">
      <button
        onClick={() => scroll(-1)}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-9 h-9 rounded-full bg-black/70 shadow-lg flex items-center justify-center text-white text-xl hover:bg-black transition-all opacity-0 group-hover:opacity-100"
        aria-label="Anterior"
      >
        ‹
      </button>
      <div
        ref={ref}
        className="flex gap-4 overflow-x-auto pb-3"
        style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {children}
      </div>
      <button
        onClick={() => scroll(1)}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-9 h-9 rounded-full bg-black/70 shadow-lg flex items-center justify-center text-white text-xl hover:bg-black transition-all opacity-0 group-hover:opacity-100"
        aria-label="Siguiente"
      >
        ›
      </button>
    </div>
  );
}

function Home() {
  const [secciones, setSecciones] = useState([]);
  const [destaques, setDestaques] = useState([]);
  const [paquetes, setPaquetes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroRegion, setFiltroRegion] = useState('');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const navProtected = (path) => isAuthenticated ? navigate(path) : navigate('/login');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const seccionesResponse = await fetch(`${process.env.REACT_APP_API_URL}/secciones-home`);
        if (seccionesResponse.ok) {
          const seccionesData = await seccionesResponse.json();
          setSecciones(seccionesData);
        }

        try {
          const destaquesResponse = await fetch(`${process.env.REACT_APP_API_URL}/destaques`);
          if (destaquesResponse.ok) {
            const destaquesData = await destaquesResponse.json();
            const destaquesConPaquetes = [];
            for (const destaque of destaquesData) {
              try {
                const pkgResponse = await fetch(`${process.env.REACT_APP_API_URL}/destaques/${destaque.id}/paquetes`);
                if (pkgResponse.ok) {
                  const paquetesDestaque = await pkgResponse.json();
                  if (paquetesDestaque && paquetesDestaque.length > 0) {
                    destaquesConPaquetes.push(destaque);
                  }
                }
              } catch (err) {
                console.warn(`Error verificando destaque ${destaque.id}:`, err);
              }
            }
            setDestaques(destaquesConPaquetes);
          }
        } catch (error) {
          console.warn('⚠️ Error cargando destaques:', error);
        }

        const paquetesResponse = await fetch(`${process.env.REACT_APP_API_URL}/paquetes`);
        if (paquetesResponse.ok) {
          let paquetesData = await paquetesResponse.json();

          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);
            const externosResponse = await fetch(
              `${process.env.REACT_APP_API_URL}/paquetes-externos`,
              { signal: controller.signal }
            );
            clearTimeout(timeoutId);

            if (externosResponse.ok) {
              const externosData = await externosResponse.json();
              if (!externosData._fallback && externosData.estado === 1 && externosData.viajes) {
                paquetesData = paquetesData.map(pkg => {
                  const externo = externosData.viajes[String(pkg.id_externo)];
                  if (externo?.impuestos) {
                    return { ...pkg, impuestos: parseFloat(externo.impuestos) || 0 };
                  }
                  return pkg;
                });
              }
            }
          } catch (error) {
            console.warn('⚠️ Sin impuestos externos:', error.message);
          }

          setPaquetes(paquetesData);
        }
      } catch (error) {
        console.error('Error:', error);
        setError('Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleBuscarFiltros = () => {
    const params = new URLSearchParams();
    if (filtroTipo) params.set('categoria', filtroTipo);
    if (filtroRegion) params.set('destaque', filtroRegion);
    navProtected(`/paquetes${params.toString() ? '?' + params.toString() : ''}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1E3A5F]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-14 w-14 border-4 border-[#2AA8B0] border-t-transparent mx-auto mb-4"></div>
          <p className="text-white/70 font-medium">Cargando contenido...</p>
        </div>
      </div>
    );
  }

  const seccion1 = renderSection('seccion_1');
  const seccion2 = renderSection('seccion_2');
  const hayCarruseles = seccion1 || seccion2;

  return (
    <>
      <Helmet>
        <title>Opciones Argentinas | Inicio</title>
        <meta name="description" content="Tu agencia líder en salidas grupales." />
        <link rel="canonical" href={`${SITE_URL}/`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/`} />
        <meta property="og:title" content="Opciones Argentinas | Inicio" />
        <meta property="og:description" content="Tu agencia líder en salidas grupales." />
        <meta property="og:image" content={`${SITE_URL}/images/og-default.jpg`} />
        <meta property="og:site_name" content="Opciones Argentinas" />
        <meta property="og:locale" content="es_AR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Opciones Argentinas | Inicio" />
        <meta name="twitter:description" content="Tu agencia líder en salidas grupales." />
        <meta name="twitter:image" content={`${SITE_URL}/images/og-default.jpg`} />
      </Helmet>

      <Header />

      {/* Degradé hero → temas */}
      <div className="h-16 bg-gradient-to-b from-black/50 to-white" />

      {/* ── SECCIÓN TEMAS ── */}
      {destaques.length > 0 && (
        <section className="pb-8 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-center text-sm font-bold text-gray-400 uppercase tracking-widest mb-6">
              Temas
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-3 md:gap-4">
              {destaques.map((destaque, idx) => {
                const iconos = [Globe, Plane, Tag, Sun, Calendar, Flag, Compass, Star, Umbrella, MapPin];
                const Icono = iconos[idx % iconos.length];
                return (
                  <button
                    key={destaque.id}
                    onClick={() => navProtected(`/paquetes?destaque=${destaque.id}`)}
                    className="flex flex-col items-center gap-2 p-3 md:p-4 rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-gray-400 transition-all"
                  >
                    <Icono size={32} strokeWidth={1.5} className="text-gray-700" />
                    <span className="text-xs md:text-sm font-semibold text-gray-700 text-center leading-tight">
                      {destaque.nombre}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Degradé temas → promociones */}
      <div className="h-16 bg-gradient-to-b from-white to-[#1E3A5F]/70" />

      {/* ── SECCIÓN AVISOS Y FLYERS DE LA SEMANA ── */}
      <section className="bg-[#1E3A5F] pb-12 md:pb-16">
        <div className="container mx-auto px-4">

          {/* Header de sección */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
            <div>
              <p className="text-[#2AA8B0] text-xs font-bold uppercase tracking-widest mb-2">
                Promociones
              </p>
              <h2 className="text-3xl md:text-5xl text-white uppercase leading-none">
                <span className="font-black">Avisos y Flyers</span>
                <span className="font-light"> de la Semana</span>
              </h2>
            </div>
            <button
              onClick={() => navProtected('/paquetes')}
              className="self-start sm:self-auto bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-3 rounded-full transition-all hover:scale-105 flex-shrink-0 shadow-lg"
            >
              Ver todos →
            </button>
          </div>

          {/* Layout: sidebar + cards */}
          <div className="flex flex-col lg:flex-row gap-8">

            {/* Sidebar de filtros */}
            <aside className="lg:w-52 flex-shrink-0">
              <div className="space-y-6">

                <div>
                  <label className="block text-white/60 text-sm italic mb-2">
                    Mostrar por Tipo:
                  </label>
                  <select
                    value={filtroTipo}
                    onChange={(e) => setFiltroTipo(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#2AA8B0]"
                  >
                    <option value="" className="text-gray-800">Todos</option>
                    <option value="nacional" className="text-gray-800">Nacional</option>
                    <option value="internacional" className="text-gray-800">Internacional</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white/60 text-sm italic mb-3">
                    Mostrar por Región:
                  </label>
                  <div>
                    <button
                      onClick={() => setFiltroRegion('')}
                      className={`w-full text-left py-2.5 border-b border-white/10 text-sm font-bold uppercase tracking-wide transition-colors ${filtroRegion === '' ? 'text-[#2AA8B0]' : 'text-white hover:text-[#2AA8B0]'}`}
                    >
                      Todas
                    </button>
                    {destaques.map((d) => (
                      <button
                        key={d.id}
                        onClick={() => setFiltroRegion(String(d.id))}
                        className={`w-full text-left py-2.5 border-b border-white/10 text-sm font-bold uppercase tracking-wide transition-colors ${filtroRegion === String(d.id) ? 'text-[#2AA8B0]' : 'text-white hover:text-[#2AA8B0]'}`}
                      >
                        {d.nombre}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleBuscarFiltros}
                  className="w-full bg-[#2AA8B0] text-white py-2.5 rounded-lg font-bold text-sm hover:bg-[#1E8A91] transition-colors"
                >
                  Buscar
                </button>
              </div>
            </aside>

            {/* Grid de cards */}
            <div className="flex-1 min-w-0">
              {/* TODO: restaurar carruseles de paquetes cuando las imágenes estén listas
              {hayCarruseles ? (
                <div className="space-y-10">
                  {seccion1 && (
                    <div>
                      <h3 className="text-white/50 text-xs font-bold uppercase tracking-widest mb-4">
                        {secciones.find(s => s.id === 1)?.titulo || 'Lo Más Vendido'}
                      </h3>
                      {seccion1}
                      <div className="text-right mt-3">
                        <button
                          onClick={() => navProtected('/paquetes')}
                          className="text-sm text-[#2AA8B0] hover:underline font-medium"
                        >
                          Ver más →
                        </button>
                      </div>
                    </div>
                  )}
                  {seccion2 && (
                    <div>
                      <h3 className="text-white/50 text-xs font-bold uppercase tracking-widest mb-4">
                        {secciones.find(s => s.id === 2)?.titulo || 'Paquetes Al Mejor Precio'}
                      </h3>
                      {seccion2}
                      <div className="text-right mt-3">
                        <button
                          onClick={() => navProtected('/paquetes')}
                          className="text-sm text-[#2AA8B0] hover:underline font-medium"
                        >
                          Ver más →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-48 text-white/30 text-sm">
                  No hay avisos disponibles por el momento.
                </div>
              )}
              */}

              {/* Imágenes temporales hasta vincular paquetes reales */}
              <div className="space-y-10">
                <div>
                  <ScrollCarousel>
                    {[1,2,3,4,5].map(n => (
                      <div
                        key={n}
                        data-card
                        className="flex-shrink-0 w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)]"
                        style={{ scrollSnapAlign: 'start' }}
                      >
                        <img
                          src={`/images/IMG${n}.jpeg`}
                          alt={`Paquete ${n}`}
                          className="w-full h-auto rounded-2xl shadow-lg hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ))}
                  </ScrollCarousel>
                  <div className="text-right mt-3">
                    <button
                      onClick={() => navProtected('/paquetes')}
                      className="text-sm text-[#2AA8B0] hover:underline font-medium"
                    >
                      Ver más →
                    </button>
                  </div>
                </div>
                <div>
                  <ScrollCarousel>
                    {[6,7,8,9,10].map(n => (
                      <div
                        key={n}
                        data-card
                        className="flex-shrink-0 w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)]"
                        style={{ scrollSnapAlign: 'start' }}
                      >
                        <img
                          src={`/images/IMG${n}.jpeg`}
                          alt={`Paquete ${n}`}
                          className="w-full h-auto rounded-2xl shadow-lg hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ))}
                  </ScrollCarousel>
                  <div className="text-right mt-3">
                    <button
                      onClick={() => navProtected('/paquetes')}
                      className="text-sm text-[#2AA8B0] hover:underline font-medium"
                    >
                      Ver más →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );

  function renderSection(sectionId) {
    const sec = secciones.find(s => s.id === parseInt(sectionId.replace('seccion_', '')));
    if (!sec) return null;

    const paquetesSeccion = sec.paquetesIds
      ?.map(id => paquetes.find(p => p.id === id && p.estado === 'activo'))
      .filter(pkg => pkg);

    if (!paquetesSeccion || paquetesSeccion.length === 0) return null;

    return (
      <ScrollCarousel>
        {paquetesSeccion.map(pkg => (
          <div
            key={pkg.id}
            data-card
            className="flex-shrink-0 w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)]"
            style={{ scrollSnapAlign: 'start' }}
          >
            <TravelCard
              paquete={pkg}
              onVerDetalles={(paquete) => {
                const params = new URLSearchParams();
                params.set('destino', paquete.destino);
                params.set('precio_max', paquete.tarifa || paquete.precio);
                navProtected(`/paquetes?${params.toString()}`);
              }}
            />
          </div>
        ))}
      </ScrollCarousel>
    );
  }
}

export default Home;
